// src/components/employer/ScheduleManagement.js
import { useState, useEffect } from 'react';
import { Container, Card, Form, Button, Alert, Spinner, Table } from 'react-bootstrap';
import { db, auth } from '../../firebase/firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import ScheduleDisplay from './ScheduleDisplay';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';

const ScheduleManagement = () => {
  const { userDetails } = useAuth();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [employees, setEmployees] = useState([]);
  const [employeeRequirements, setEmployeeRequirements] = useState({});
  const [schedules, setSchedules] = useState([]);
  const [availabilityData, setAvailabilityData] = useState({});
  const [businessHours, setBusinessHours] = useState({
    start: '09:00',
    end: '17:00'
  });
  const [numberOfOptions, setNumberOfOptions] = useState(5); // Default to 5 options


  // Fetch employees and their availability
  useEffect(() => {
    const fetchEmployeeData = async () => {
      try {
        if (!userDetails?.communityId) {
          console.log('No community ID available');
          return;
        }

        // Fetch employees
        const employeesRef = collection(db, 'users');
        const q = query(
          employeesRef,
          where('communityId', '==', userDetails.communityId),
          where('userType', '==', 'employee')
        );
        
        const employeeSnapshot = await getDocs(q);
        const employeeData = [];
        const initialRequirements = {};
        
        employeeSnapshot.forEach(doc => {
          const data = doc.data();
          employeeData.push({
            id: doc.id,
            ...data
          });
          initialRequirements[doc.id] = {
            daysPerWeek: 5,
            minHoursPerDay: 6,
            maxHoursPerDay: 8
          };
        });

        setEmployees(employeeData);
        setEmployeeRequirements(initialRequirements);

        // Fetch availability for each employee
        const availabilityRef = collection(db, 'availability');
        const availData = {};

        for (const emp of employeeData) {
          const availQuery = query(availabilityRef, where('userId', '==', emp.id));
          const availSnapshot = await getDocs(availQuery);
          
          if (!availSnapshot.empty) {
            availData[emp.id] = availSnapshot.docs[0].data().availability;
          }
        }

        setAvailabilityData(availData);
        console.log('Fetched availability data:', availData);
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching employee data:', error);
        setError('Failed to load employee data');
        setLoading(false);
      }
    };

    fetchEmployeeData();
  }, [userDetails]);

  const handleRequirementChange = (employeeId, field, value) => {
    setEmployeeRequirements(prev => ({
      ...prev,
      [employeeId]: {
        ...prev[employeeId],
        [field]: parseInt(value, 10)
      }
    }));
  };

  const initializeEmployeeRequirements = (employeeId) => {
    if (!employeeRequirements[employeeId]) {
      setEmployeeRequirements(prev => ({
        ...prev,
        [employeeId]: {
          daysPerWeek: 5,
          minHoursPerDay: 6,
          maxHoursPerDay: 8,
          preferredStartTime: formatTimeForInput(businessHours.start),
          preferredEndTime: formatTimeForInput(businessHours.end)
        }
      }));
    }
  };

  const formatTimeForInput = (time) => {
    if (!time) return '09:00';
    // Ensure time is in HH:mm format
    const [hours, minutes] = time.split(':');
    return `${hours.padStart(2, '0')}:${minutes ? minutes.padStart(2, '0') : '00'}`;
  };

  const handleTimeChange = (employeeId, field, value) => {
    try {
      // Handle time input
      const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
      if (!timeRegex.test(value)) {
        // If just hours are entered, add ':00'
        if (/^([0-1]?[0-9]|2[0-3])$/.test(value)) {
          value = `${value.padStart(2, '0')}:00`;
        } else {
          return; // Invalid format, don't update
        }
      }
  
      setEmployeeRequirements(prev => ({
        ...prev,
        [employeeId]: {
          ...prev[employeeId],
          [field]: value
        }
      }));
    } catch (error) {
      console.error('Error handling time change:', error);
    }
  };


  const generateScheduleSuggestions = async () => {
    try {
      setGenerating(true);
      setError('');

      const token = await auth.currentUser.getIdToken();

      // Prepare the request data
      const requestData = {
        employees: employees.map(emp => ({
          id: emp.id,
          name: emp.name,
          requirements: {
            ...employeeRequirements[emp.id],
            availability: availabilityData[emp.id] || {
              monday: { isAvailable: true, startTime: employeeRequirements[emp.id]?.preferredStartTime, endTime: employeeRequirements[emp.id]?.preferredEndTime },
              tuesday: { isAvailable: true, startTime: employeeRequirements[emp.id]?.preferredStartTime, endTime: employeeRequirements[emp.id]?.preferredEndTime },
              wednesday: { isAvailable: true, startTime: employeeRequirements[emp.id]?.preferredStartTime, endTime: employeeRequirements[emp.id]?.preferredEndTime },
              thursday: { isAvailable: true, startTime: employeeRequirements[emp.id]?.preferredStartTime, endTime: employeeRequirements[emp.id]?.preferredEndTime },
              friday: { isAvailable: true, startTime: employeeRequirements[emp.id]?.preferredStartTime, endTime: employeeRequirements[emp.id]?.preferredEndTime },
              saturday: { isAvailable: true, startTime: employeeRequirements[emp.id]?.preferredStartTime, endTime: employeeRequirements[emp.id]?.preferredEndTime },
              sunday: { isAvailable: true, startTime: employeeRequirements[emp.id]?.preferredStartTime, endTime: employeeRequirements[emp.id]?.preferredEndTime }
            }
          }
        })),
        businessHours,
        numberOfOptions,
        daysOfWeek: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
      };

      console.log('Sending schedule generation request:', requestData);

      const response = await fetch('http://localhost:5001/api/schedules/generate-schedule', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.details || data.error || 'Failed to generate schedule');
      }

      setSchedules(data.options);

    } catch (error) {
      console.error('Detailed error:', error);
      setError(error.message);
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center min-vh-100">
        <Spinner animation="border" variant="primary" />
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <Card className="mb-4">
        <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
          <h4 className="mb-0">Schedule Management</h4>
        </Card.Header>
        <Card.Body>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          
          <Form className="mb-4">
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Business Hours Start</Form.Label>
                <Form.Control
                  type="time"
                  value={formatTimeForInput(businessHours.start)}
                  onChange={(e) => setBusinessHours(prev => ({ 
                    ...prev, 
                    start: formatTimeForInput(e.target.value)
                  }))}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Business Hours End</Form.Label>
                <Form.Control
                  type="time"
                  value={formatTimeForInput(businessHours.end)}
                  onChange={(e) => setBusinessHours(prev => ({ 
                    ...prev, 
                    end: formatTimeForInput(e.target.value)
                  }))}
                />
              </Form.Group>
            </Col>
          </Row>
          <Form.Group className="mb-3">
            <Form.Label>Number of Schedule Options</Form.Label>
            <Form.Control
              type="number"
              min="1"
              max="10"
              value={numberOfOptions}
              onChange={(e) => setNumberOfOptions(parseInt(e.target.value, 10))}
            />
          </Form.Group>
        </Form>

          <h5 className="mb-3">Employee Requirements</h5>
          
          {employees.length === 0 ? (
            <Alert variant="info">
              No employees found. Add employees to generate schedules.
            </Alert>
          ) : (
            <>
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>Employee Name</th>
                    <th>Days per Week</th>
                    <th>Min Hours/Day</th>
                    <th>Max Hours/Day</th>
                    <th>Preferred Start</th>
                    <th>Preferred End</th>
                  </tr>
                </thead>
                <tbody>
                  {employees.map(employee => {
                    initializeEmployeeRequirements(employee.id);
                    return (
                      <tr key={employee.id}>
                        <td>{employee.name}</td>
                        <td>
                          <Form.Control
                            type="number"
                            min="1"
                            max="7"
                            value={employeeRequirements[employee.id]?.daysPerWeek || 5}
                            onChange={(e) => handleRequirementChange(employee.id, 'daysPerWeek', e.target.value)}
                          />
                        </td>
                        <td>
                          <Form.Control
                            type="number"
                            min="4"
                            max="12"
                            value={employeeRequirements[employee.id]?.minHoursPerDay || 6}
                            onChange={(e) => handleRequirementChange(employee.id, 'minHoursPerDay', e.target.value)}
                          />
                        </td>
                        <td>
                          <Form.Control
                            type="number"
                            min="4"
                            max="12"
                            value={employeeRequirements[employee.id]?.maxHoursPerDay || 8}
                            onChange={(e) => handleRequirementChange(employee.id, 'maxHoursPerDay', e.target.value)}
                          />
                        </td>
                        <td>
                        <Form.Control
                          type="time"
                          value={formatTimeForInput(employeeRequirements[employee.id]?.preferredStartTime)}
                          onChange={(e) => handleTimeChange(
                            employee.id,
                            'preferredStartTime',
                            e.target.value
                          )}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="time"
                          value={formatTimeForInput(employeeRequirements[employee.id]?.preferredEndTime)}
                          onChange={(e) => handleTimeChange(
                            employee.id,
                            'preferredEndTime',
                            e.target.value
                          )}
                        />
                      </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>

              <div className="d-grid gap-2">
                <Button
                  variant="primary"
                  onClick={generateScheduleSuggestions}
                  disabled={generating}
                >
                  {generating ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Generating Schedules...
                    </>
                  ) : (
                    'Generate Schedule Suggestions'
                  )}
                </Button>
              </div>
            </>
          )}
        </Card.Body>
      </Card>

      {schedules.length > 0 && (
        <Card>
          <Card.Header className="bg-success text-white">
            <h5 className="mb-0">Schedule Suggestions ({schedules.length} options)</h5>
          </Card.Header>
          <Card.Body>
            <ScheduleDisplay schedules={schedules} />
          </Card.Body>
        </Card>
      )}
    </Container>
  );
};

export default ScheduleManagement;